import { isNil } from 'lodash-es'
import { Validate } from 'react-hook-form'

import { Data } from '../../../domain'
import { FormatsConfig, FormConfig, ValidationConfig } from '../../../state/configuration'
import { isInputConfig } from '../config'
import { isNotNil, raiseError, required } from './helpers'
import { ValidationFunctions } from './types'
import {
  checkbox,
  checkboxEligibility,
  checkboxPromotion,
  date,
  datePicker,
  email,
  monthsRange,
  name,
  numberText,
  phoneNumber,
  radio,
  text,
  vehicleRegistration,
  vehicleTabs,
  yearMonth,
} from './validators'

const validationMap: Record<string, ValidationFunctions> = {
  checkbox,
  checkboxPromotion,
  checkboxEligibility,
  date,
  datePicker,
  email,
  name,
  numberText,
  phoneNumber,
  radio,
  text,
  vehicleRegistration,
  vehicleTabs,
  yearMonth,
  monthsRange,
}

export const getValidationRules = (
  type: string,
  validation: ValidationConfig | undefined,
  config: FormConfig,
  formatsGlobalConfig?: FormatsConfig,
): Record<string, Validate<unknown, unknown>> => {
  const dynamicConfig = config.filter(isInputConfig)

  const validationRules = validationMap[type] ?? { _self: { required } }

  const { _self, ...others } = validation ?? {}

  const selfRules: Record<string, Validate<unknown, unknown>> = Object.entries(
    _self ?? {},
  ).reduce(
    (validation, [rule, option]) =>
      isNotNil(validationRules._self?.[rule])
        ? {
            ...validation,
            [rule]: validationRules._self[rule](
              formatsGlobalConfig && rule === 'format' && option === true
                ? formatsGlobalConfig[type]
                : option,
            ),
          }
        : raiseError(`Could not find validation rule ${rule}`),
    {},
  )

  const otherRules: Record<string, Validate<unknown, unknown>> = Object.entries(
    others,
  ).reduce((inputRules, [inputName, rules]) => {
    const input = dynamicConfig.find((it) => it.name === inputName)

    if (isNil(input)) {
      throw new Error(
        `No input exists with name ${inputName}. Cannot load validation rules`,
      )
    }

    const validationRule = validationRules[input.type] ?? { required }

    return Object.entries(rules ?? {}).reduce(
      (validation, [rule, option]) =>
        isNotNil(validationRule[rule])
          ? {
              ...validation,
              [`${inputName}_${rule}`]: validationRule[rule](option, inputName),
            }
          : raiseError(
              `Could not find validation rule for type ${type}, input type ${input.type} and rule ${rule}`,
            ),
      inputRules,
    )
  }, {})

  return {
    ...selfRules,
    ...otherRules,
  }
}

export const isValidForm = (form: FormConfig, data: Data) =>
  form.filter(isInputConfig).every((input) => {
    const validationRules = getValidationRules(input.type, input.validation, form)

    return Object.values(validationRules).every((rule) => rule(data[input.name], data))
  })
